import { atom } from "jotai";
import { questionnaireValidationsAtom } from "./questionnaireValidationsAtom";
import { extractValidationDependencies } from "../../../components/Questionnaire/Section/Question/QuestionFactory/extractValidationDependencies";

type ValidationDependenciesMap = Record<string, string[]>;

const EMPTY_DEPENDENCIES_MAP: ValidationDependenciesMap = {};

/**
 * Derived atom that computes validation dependencies map from validations.
 *
 * This atom automatically updates when validations change, creating a memoized
 * lookup map of question names to their validation dependencies.
 *
 * @example
 * ```typescript
 * // Given validations:
 * [{ id: "confirm_email", operator: "eq", expression: "${email}" }]
 *
 * // Returns:
 * { "confirm_email": ["confirm_email", "email"] }
 * ```
 */
export const questionnaireValidationDependenciesAtom = atom(get => {
  const validations = get(questionnaireValidationsAtom);

  if (!validations || validations.length === 0) {
    return EMPTY_DEPENDENCIES_MAP;
  }

  const map: ValidationDependenciesMap = {};

  for (const validation of validations) {
    // Extract all dependencies from this validation rule
    const dependencies = extractValidationDependencies(validation);

    // The validation's id is the question it applies to
    if (validation.id) {
      map[validation.id] = dependencies;
    }
  }

  return map;
});
